import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { LoginService } from '../login.service';
import { BlockService } from '../block.service';
import { TranslateService } from "@ngx-translate/core";
import { LanguageService } from "../language.service";

@Component({
    selector: 'app-block',
    templateUrl: './block.component.html',
    styleUrls: ['./block.component.scss'],
    standalone: false
})
export class BlockComponent implements OnInit {
  @Output() closed = new EventEmitter<boolean>();
  @Input('event') event
  @Input("selectedResourceId") selectedResourceId: string = "";
  @Input("isMultiSelect") isMultiSelect: boolean = false;
  blockForm:UntypedFormGroup
  pageDirection: string = "ltr";

  startOptionalsHours = [];
  showDeleteAlert=false;
  error;
  formattedDuration = { days: 0, hours: 0, minutes: 0 };
  daysList = [
    { label: this.translate.instant(`appointments.gui.labels.days.sunday`), value: 0 },
    { label: this.translate.instant(`appointments.gui.labels.days.monday`), value: 1 },
    { label: this.translate.instant(`appointments.gui.labels.days.tuesday`), value: 2 },
    { label: this.translate.instant(`appointments.gui.labels.days.wednesday`), value: 3 },
    { label: this.translate.instant(`appointments.gui.labels.days.thursday`), value: 4 },
    { label: this.translate.instant(`appointments.gui.labels.days.friday`), value: 5 },
    { label: this.translate.instant(`appointments.gui.labels.days.saturday`), value: 6 },
  ];
  
  constructor(
    private fb: UntypedFormBuilder,
    public loginService: LoginService,
    public blockService: BlockService,
    public translate: TranslateService,
    public languageService: LanguageService,
  ) {}

  ngOnInit(): void {
    this.pageDirection = this.languageService.getPageDirection();

    this.resetBlockForm();
    this.blockForm.get('duration').valueChanges.subscribe(value => {
      this.formattedDuration = this.calculateDuration(value);
    });
    this.loadBlockForm()
    this.generateTimes(this.blockService.startDate);
    this.subscribeToFormChanges();

    this.blockForm.get('isRecurring')?.valueChanges.subscribe((isRecurring) => {
      if (isRecurring) {
        const today = new Date().getDay(); // 0 = Sunday, ..., 6 = Saturday
        this.blockForm.get('daysOfWeek')?.setValue([today]);
      } else {
        this.blockForm.get('daysOfWeek')?.setValue([]);
      }
    });
  }

  resetBlockForm(){
    this.blockForm = this.fb.group({
      AutoIndex: [''],
      blockStartDate: [''],
      startHour: [''],
      duration: [''],
      comment: [null],
      UID: [[]],
      blockEndDate: [''],
      endHour: [''],
      RID: [[]],
      isRecurring: [false],
      daysOfWeek: [[]],
      repeatUntil: [null],
    });    
  }

  
subscribeToFormChanges() {
  // Track changes on both start and end dates or hours
  const startDateControl = this.blockForm.get('blockStartDate');
  const startHourControl = this.blockForm.get('startHour');
  const endDateControl = this.blockForm.get('blockEndDate');
  const endHourControl = this.blockForm.get('endHour');

  startDateControl.valueChanges.subscribe(() => this.updateDuration());
  startHourControl.valueChanges.subscribe(() => this.updateDuration());
  endDateControl.valueChanges.subscribe(() => this.updateDuration());
  endHourControl.valueChanges.subscribe(() => this.updateDuration());
}


updateDuration() {
  const startValue = this.blockForm.get('blockStartDate').value;
  const startTime = this.blockForm.get('startHour').value;
  const endValue = this.blockForm.get('blockEndDate').value;
  const endTime = this.blockForm.get('endHour').value;

  if (startValue && startTime && endValue && endTime) {
    const startDate = new Date(startValue);
    const endDate = new Date(endValue);

    // Adjust the hours and minutes based on input
    const [startHours, startMinutes] = startTime.split(':');
    const [endHours, endMinutes] = endTime.split(':');
    startDate.setHours(+startHours, +startMinutes);
    endDate.setHours(+endHours, +endMinutes);

    // Calculate duration in minutes
    const durationInMinutes = (endDate.getTime() - startDate.getTime()) / 1000 / 60;
    this.blockForm.get('duration').setValue(durationInMinutes);
    this.formattedDuration = this.calculateDuration(durationInMinutes);
  }
}

loadBlockForm() {
  this.initialStartBlockHour();
  this.initialEndBlockHour();
  this.blockForm.patchValue({
    blockStartDate: this.blockService.startDate,
    blockEndDate: this.blockService.endDate,
    duration: this.blockService.duration,
    UID: this.isMultiSelect
      ? this.loginService.premissions?.ApptsByRoom ? [] : [this.selectedResourceId]
      : [this.loginService.doctor],
    RID: this.isMultiSelect
      ? this.loginService.premissions?.ApptsByRoom ? [this.selectedResourceId] : []
      : [this.loginService.room],
  });

  if (this.event) {
      this.blockForm.patchValue({
        AutoIndex: this.blockService.AutoIndex,
        UID: this.event.DocUID !== null ? [this.event.DocUID] : [],
        RID: this.event.RID !== null ? [this.event.RID] : [],
        comment: this.event.comment,
        isRecurring: this.event.isRecurring || false,
        daysOfWeek: this.event.daysOfWeek
          ? this.event.daysOfWeek.split(',').map((d) => +d)
          : [],
        repeatUntil: this.event.repeatUntil ? new Date(this.event.repeatUntil) : null,
        blockStartDate: new Date(this.event.aptTime),
        startHour: this.extractHour(this.event.aptTime),
        blockEndDate: new Date(this.event.blockEndTime),
        endHour: this.extractHour(this.event.blockEndTime),
        duration: this.event.aptlength,
      });
  }
  
}
extractHour(dateString: string): string {
  const date = new Date(dateString);
  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
}


  
  initialEndBlockHour(){
    const endDate = this.blockService.endDate;
    if (endDate) {
      const hours = endDate.getHours().toString().padStart(2, "0");
      const minutes = endDate.getMinutes();
     const endHour = `${hours}:${minutes.toString().padStart(2, "0")}`; 
      this.blockForm.get('endHour').setValue(endHour) 
    }
  }


  initialStartBlockHour(){
    const startDate = this.blockService.startDate;
    if (startDate) {
      const hours = startDate.getHours().toString().padStart(2, "0");
      const minutes = startDate.getMinutes();
     const startHour = `${hours}:${minutes.toString().padStart(2, "0")}`; 
      this.blockForm.get('startHour').setValue(startHour) 
    }
  }

  async deleteBlock() {
    try {
      const result = await this.blockService.delete();
      if (result) {
        this.closeBlockModal();
      }
    } catch (error) {
      console.error('Error during the deletion process:', error);
    }
  }

  
  async updateBlock() {
    try {
      const result = await this.blockService.updateBlock(this.blockForm.value);
      if (result['status']=='ok') {
        this.error=false;
        this.closeBlockModal();

      }
    } catch (error) {
      console.error('Error during the update block:', error);
      this.error=true
    }
  }

  
  generateTimes(startDay) {
    const startHour = startDay.getHours();
    this.startOptionalsHours = [];
    for (let minute = 0; minute < 60; minute += 5) {
      let time = `${startHour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
      this.startOptionalsHours.push(time);
    }
  }

  closeBlockModal(){
   this.closed.emit()
  }

  saveBlock(){
    this.blockService.addBlock(this.blockForm.value).subscribe((data)=>{
      this.closeBlockModal()
    
    })
  }



calculateDuration(durationInMinutes: number) {
  const minutesInADay = 60 * 24;
  const minutesInAnHour = 60;

  const days = Math.floor(durationInMinutes / minutesInADay);
  durationInMinutes -= days * minutesInADay;

  const hours = Math.floor(durationInMinutes / minutesInAnHour);
  durationInMinutes -= hours * minutesInAnHour;

  const minutes = durationInMinutes;

  return { days, hours, minutes };
}


ngOnDestroy(): void {


}

}
